import { t } from 'i18next';
import type {
  DataTableFilter,
  DataTableRowAction,
  DataTableSearch,
  DataTableSelectAllModel,
  DataTableSelectionModel,
} from './types';

export function selectionCountLabel(selection: DataTableSelectionModel): string {
  const { selectAll, count } = selection;
  if (!selectAll?.active) return t('tables:selection.count', { count });
  return selectAll.total === undefined
    ? t('tables:selection.all_selected')
    : t('tables:selection.count', { count: selectAll.total });
}

export function getSelectAllAction(
  selection: DataTableSelectionModel,
): { label: string; onClick: () => void } | undefined {
  const { selectAll, count } = selection;
  if (!selectAll) return undefined;

  if (selectAll.active) {
    return { label: t('tables:selection.undo'), onClick: selection.clear };
  }

  if (selectAll.total !== undefined && selectAll.total <= count) return undefined;

  return { label: selectAllLabel(selectAll), onClick: selectAll.apply };
}

function selectAllLabel(selectAll: DataTableSelectAllModel): string {
  return selectAll.total === undefined
    ? t('tables:selection.select_all')
    : t('tables:selection.select_all_matching', { count: selectAll.total });
}

export function nextSelection(
  selected: readonly string[],
  value: string,
  multiple?: boolean,
): string[] {
  if (multiple) {
    return selected.includes(value)
      ? selected.filter((entry) => entry !== value)
      : [...selected, value];
  }
  return selected.includes(value) ? [] : [value];
}

const TREE_INDENT_STEPS = ['ps-0', 'ps-3', 'ps-6', 'ps-9', 'ps-12', 'ps-15'] as const;

export function treeIndentClass(depth: number): string {
  const step = Math.min(Math.max(depth, 0), TREE_INDENT_STEPS.length - 1);
  return TREE_INDENT_STEPS[step] ?? TREE_INDENT_STEPS[0];
}

export function getPageRange(
  page: number,
  pageSize: number,
  total?: number,
): { first: number; last: number } | undefined {
  if (total === 0) {
    return undefined;
  }
  const first = (page - 1) * pageSize + 1;
  const last = total === undefined ? page * pageSize : Math.min(page * pageSize, total);
  return { first, last };
}

export function clampPage(value: number | string, totalPages: number): number {
  const parsed = typeof value === 'string' ? Number.parseInt(value, 10) : Math.trunc(value);
  if (!Number.isFinite(parsed)) {
    return 1;
  }
  return Math.min(Math.max(parsed, 1), totalPages);
}

export function splitRowActions(actions: DataTableRowAction[]): {
  actions: DataTableRowAction[];
  moreActions: DataTableRowAction[];
} {
  const visible = actions.filter((action) => !action.hidden);
  return {
    actions: visible.filter((action) => !action.inMoreMenu),
    moreActions: visible.filter((action) => action.inMoreMenu),
  };
}

export function clearAllHandler(
  search: DataTableSearch | undefined,
  filters: readonly DataTableFilter[],
): () => void {
  return () => {
    search?.onChange('');
    for (const filter of filters) {
      filter.onChange([]);
    }
  };
}

export function hasActiveFilters(
  search: DataTableSearch | undefined,
  filters: readonly DataTableFilter[],
): boolean {
  return Boolean(search?.value) || filters.some((filter) => filter.value.length > 0);
}

export function getAppliedFilter(
  filter: DataTableFilter,
): { label: string; onRemove(): void } | undefined {
  if (filter.value.length === 0) return undefined;
  const labels = filter.value.map(
    (value) => filter.options.find((option) => option.id === value)?.label ?? value,
  );
  return {
    label: `${filter.label}: ${labels.join(', ')}`,
    onRemove: () => filter.onChange([]),
  };
}
